import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ChevronRight, Truck, BrainCircuit, MapPin, ShieldCheck, UserCheck, Coins, Handshake, BarChart3, Users, Boxes, Flame } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import GlitterIcon from "../components/GlitterIcon";
import GlassRevealOverlay from "../components/GlassRevealOverlay";
import LottieIcon from "../components/LottieIcon";
import everyAgeAnimation from "../assets/everyage-lottie.json";
import refillBagAnimation from "../assets/refill-bag-lottie.json";
import recordAnimation from "../assets/record-lottie.json";
import productAnimation from "../assets/product-lottie.json";
import wholesaleAnimation from "../assets/wholesale-lottie.json";
import retailAnimation from "../assets/retail-lottie.json";
import wellnessAnimation from "../assets/wellness-lottie.json";
import surgicalAnimation from "../assets/surgical-lottie.json";
import franchiseAnimation from "../assets/franchise-lottie.json";

// Scroll reveal — attaches Intersection Observer to all .reveal* elements
function useScrollReveal() {
  useEffect(() => {
    // Reset all visible classes so animations replay on every mount/navigation
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );

    // Small delay so DOM is painted before observing
    const timer = setTimeout(() => {
      els.forEach((el) => observer.observe(el));
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);
}

// Service cards — keyed by title (not index) so icon/color/group stay correct
// no matter how home.services.items is reordered in the locale file.
const SERVICE_GROUPS = ["Patient & Customer", "Investor & Analysts", "In & Out Supply", "Hot Spots"];
// iOS-style icon tile: every icon renders at the same visual size with a
// consistent margin inside its rounded square (Apple HIG ~18-20% safe-area
// padding). Each Lottie's own artwork is pre-fit to its canvas edge (baked
// in via scripts/lottie-normalize.js, not a runtime scale), so no per-icon
// crop factor is needed here anymore.
const SERVICE_ICON_SIZE = "w-12 h-12";
const serviceMeta: Record<string, { lottieData: object; bg: string; group: string }> = {
  "Every Age, Covered": { lottieData: everyAgeAnimation, bg: "bg-[radial-gradient(circle,_#ffffff_0%,_#fff1f1_50%,_#fff7ed_100%)] shadow-md", group: "Patient & Customer" },
  "Every Refill, Secured": { lottieData: refillBagAnimation, bg: "bg-teal-50 shadow-md", group: "Patient & Customer" },
  "Every Record, Decoded": { lottieData: recordAnimation, bg: "bg-cyan-50 shadow-md", group: "Patient & Customer" },
  "Every Product, Assured": { lottieData: productAnimation, bg: "bg-indigo-50 shadow-md", group: "Patient & Customer" },
  "Every Patient, Remembered":   { lottieData: retailAnimation,     bg: "bg-rose-50 shadow-md",      group: "Investor & Analysts" },
  "Every Pharmacy, Supplied": { lottieData: wholesaleAnimation,  bg: "bg-blue-50 shadow-md",      group: "Investor & Analysts" },
  "Every Ambition, Backed": { lottieData: franchiseAnimation,  bg: "bg-secondary-50 shadow-md", group: "Investor & Analysts" },
  "Every Feeling, Addressed":      { lottieData: wellnessAnimation,  bg: "bg-emerald-50 shadow-md",   group: "Hot Spots" },
  "Every Recovery, Supported":      { lottieData: surgicalAnimation,  bg: "bg-amber-50 shadow-md",     group: "Hot Spots" },
};
const groupHeaderIcons: Record<string, typeof Truck> = {
  "Patient & Customer": Users,
  "Investor & Analysts": TrendingUp,
  "In & Out Supply": Boxes,
  "Hot Spots": Flame,
};
const groupAccents: Record<string, { bg: string; icon: string; ring: string }> = {
  "Patient & Customer": { bg: "bg-primary-50", icon: "text-primary-600", ring: "from-primary-400 to-primary-600" },
  "Investor & Analysts": { bg: "bg-secondary-50", icon: "text-secondary-600", ring: "from-secondary-400 to-secondary-600" },
  "In & Out Supply": { bg: "bg-blue-50", icon: "text-blue-600", ring: "from-blue-400 to-blue-600" },
  "Hot Spots": { bg: "bg-emerald-50", icon: "text-emerald-600", ring: "from-emerald-400 to-emerald-600" },
};

// Why APlus — unique icons per point matching the content
// Points: Customer-first, Pan-India vision, Proprietary AI, High savings, Data-driven, Franchise partners, Stock what's good for you
const whyUsIcons = [UserCheck, MapPin, BrainCircuit, Coins, BarChart3, Handshake, ShieldCheck];
const whyUsColors = [
  { icon: "text-blue-600",   bg: "from-blue-500 to-indigo-600" },
  { icon: "text-rose-600",   bg: "from-rose-500 to-primary-600" },
  { icon: "text-violet-600", bg: "from-violet-500 to-purple-600" },
  { icon: "text-amber-600",  bg: "from-amber-500 to-orange-500" },
  { icon: "text-cyan-600",   bg: "from-cyan-500 to-blue-600" },
  { icon: "text-secondary-600", bg: "from-secondary-500 to-primary-500" },
  { icon: "text-emerald-600",bg: "from-emerald-500 to-teal-600" },
];

const Home = () => {
  const { t } = useLanguage();
  const home = t.home;
  useScrollReveal();

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-white via-slate-50 to-gray-100">
        {/* Silver shimmer blobs */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-primary-100/60 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-slate-200/80 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-10 w-64 h-64 bg-secondary-100/50 rounded-full blur-3xl"></div>
          <div className="absolute top-20 right-10 w-72 h-72 bg-gray-200/60 rounded-full blur-3xl"></div>
        </div>
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>

        {/* Tagline anchored to the bottom of the hero, centered — desktop only; content can exceed one screen on mobile/tablet, so it's shown inline there instead (see below) */}
        <div className="hidden lg:flex absolute bottom-10 left-1/2 -translate-x-1/2 w-full px-4 items-center justify-center gap-2 z-10">
          <p
            className="text-sm sm:text-base font-bold tracking-wide"
            style={{
              background: 'linear-gradient(90deg, #000000, #e51c1c, #f97316)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {home.hero.tagline}
          </p>
          <GlitterIcon color="red" className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2.5fr] gap-10 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2.5 bg-primary-50 border border-primary-200 rounded-full pl-4 pr-5 py-2 mb-8 shadow-sm">
                <span className="relative inline-flex">
                  <GlitterIcon color="red" />
                </span>
                <span className="text-sm font-semibold text-primary-700">{home.hero.badge}</span>
                <span className="w-px h-4 bg-primary-300"></span>
                <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">{home.hero.badgeTranslation}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-1 text-gray-900">
                {home.hero.headline1} <br />
                <span className="bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent">{home.hero.headline2}</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                {home.hero.subtext}
              </p>
              {/* Catchy pre-CTA phrase */}
              <div className="mb-5 flex items-center justify-center lg:justify-start gap-3">
                <span className="h-px w-8 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-full"></span>
                <p className="text-sm font-semibold tracking-wide"
                  style={{
                    background: 'linear-gradient(90deg, #e51c1c, #f97316)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  There's a lot happening — here's something new today ↓
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <Link to="/about" className="flex items-center justify-center gap-2 w-full sm:w-auto border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  {home.hero.cta2} <ChevronRight className="w-4 h-4" />
                </Link>
                <Link to="/pharmacy" className="flex items-center justify-center gap-2 w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  {home.hero.cta1} <GlitterIcon color="gold" />
                </Link>
              </div>

              {/* Stats — mobile only (shown below CTAs) */}
              <div className="mt-10 grid grid-cols-2 gap-4 lg:hidden">
                {home.stats.map((stat, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 text-center shadow-md border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-primary-600 mb-0.5">{stat.value}</div>
                    <div className="text-gray-700 font-semibold text-xs">{stat.label}</div>
                    <div className="text-gray-600 text-xs mt-1">{stat.desc}</div>
                  </div>
                ))}
              </div>

              {/* Tagline — mobile/tablet only, inline so it never overlaps the stats above (see the absolute version for lg+) */}
              <p className="lg:hidden mt-8 text-sm font-bold tracking-wide text-center">
                <span
                  style={{
                    background: 'linear-gradient(90deg, #000000, #e51c1c, #f97316)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {home.hero.tagline}
                </span>{' '}
                <GlitterIcon color="red" className="w-4 h-4 inline-block align-middle" />
              </p>
            </div>

            {/* Stats Card — right column, desktop only */}
            <div className="hidden lg:block">
              <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-8 shadow-2xl shadow-gray-200/60">
                <div className="grid grid-cols-2 gap-4">
                  {home.stats.map((stat, i) => (
                    <div key={i} className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-5 text-center border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="font-heading text-3xl font-bold text-primary-600 mb-1">{stat.value}</div>
                      <div className="text-gray-700 font-semibold text-sm">{stat.label}</div>
                      <div className="text-gray-600 text-xs mt-1">{stat.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* iOS-style glass intro — the window loads frosted, then clears to normal */}
        <GlassRevealOverlay />
      </section>

      {/* Services Section */}
      <section className="py-14 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-8 lg:mb-10">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              {home.services.badge}
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              <span className="block mb-3">{home.services.title}</span>
              <span className="gradient-text">{home.services.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">{home.services.subtitle}</p>
          </div>
          {SERVICE_GROUPS.map((group) => {
            const items = home.services.items.filter((service) => serviceMeta[service.title]?.group === group);
            if (items.length === 0) return null;
            const GroupIcon = groupHeaderIcons[group];
            const accent = groupAccents[group];
            const gridClass = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch";
            return (
              <div key={group} className="mb-12 last:mb-0">
                <div className="reveal flex items-center gap-4 mb-7">
                  <div className={`w-12 h-12 rounded-2xl ${accent.bg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <GroupIcon className={`w-6 h-6 ${accent.icon}`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-gray-900 leading-tight">{group}</h3>
                    <span className={`inline-block mt-1.5 h-1 w-10 rounded-full bg-gradient-to-r ${accent.ring}`}></span>
                  </div>
                </div>
                <div className={gridClass}>
                  {items.map((service, i) => {
                    const meta = serviceMeta[service.title];
                    const stagger = `stagger-${(i % 8) + 1}`;
                    return (
                      <div
                        key={service.title}
                        className={`reveal card group cursor-pointer flex flex-col h-full ${stagger}`}
                      >
                        <div className={`w-14 h-14 ${meta.bg} rounded-2xl flex items-center justify-center mb-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          <LottieIcon animationData={meta.lottieData} className={SERVICE_ICON_SIZE} />
                        </div>
                        <h3 className="font-heading font-bold text-gray-900 text-lg mb-3">
                          {service.title.includes(",") ? (
                            <>
                              {service.title.split(",")[0]},{" "}
                              <span
                                style={{
                                  background: "linear-gradient(90deg, #e51c1c, #f97316)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }}
                              >
                                {service.title.split(",")[1].trim()}
                              </span>
                            </>
                          ) : (
                            service.title
                          )}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed flex-1">{service.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section Header — same style as Services section */}
          <div className="reveal text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
              {home.whyUs.badge}
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              <span className="block mb-3">{home.whyUs.title}</span>
              <span className="gradient-text">{home.whyUs.titleHighlight}</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-secondary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
            <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">{home.whyUs.body}</p>
          </div>

          {/* Why Points Grid — icon on top, text below */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {home.whyUs.points.map((item, i) => {
              const WhyIcon = whyUsIcons[i];
              const wColor = whyUsColors[i];
              const stagger = `stagger-${i + 1}`;
              return (
                <div key={i} className={`reveal group flex flex-col items-center text-center bg-white hover:bg-white border border-gray-100 hover:border-secondary-200 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5 ${stagger}`}>
                  {/* Icon on TOP */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${wColor.bg} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <WhyIcon className="w-7 h-7 text-white" />
                  </div>
                  {/* Number badge */}
                  <span className="text-xs font-bold text-gray-300 mb-2 tracking-widest">0{i + 1}</span>
                  {/* Text BELOW */}
                  <span className="text-gray-700 text-sm leading-relaxed font-medium">{item}</span>
                </div>
              );
            })}
          </div>

          <div className="reveal mt-4 flex justify-center">
            <Link to="/about" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5">
              {home.whyUs.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 lg:py-24 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="reveal font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 lg:mb-6">
            {home.cta.title}
          </h2>
          <p className="reveal stagger-2 text-white/80 text-base lg:text-lg max-w-2xl mx-auto mb-8 lg:mb-10">
            {home.cta.subtitle}
          </p>
          <div className="reveal stagger-3 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center items-center">
            <Link to="/investors" className="w-full sm:w-auto bg-white text-primary-700 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 text-center">
              {home.cta.btn1}
            </Link>
            <Link to="/franchise" className="w-full sm:w-auto btn-outline text-center justify-center">
              {home.cta.btn2} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="w-full sm:w-auto border-2 border-white/50 text-white hover:border-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 text-center">
              {home.cta.btn3}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
